import { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { BookOpen, MapPin, CreditCard, ArrowLeft, Calendar, Navigation, Truck, User } from 'lucide-react';
import { toast } from 'sonner';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { checkRentEligibility } from '../lib/rentEligibility';
import { saveCheckoutResume, loadCheckoutResume, clearCheckoutResume } from '../lib/checkoutResume';
import RentCriteriaDialog from '../components/RentCriteriaDialog';
import { calculateRentalCharges } from '../lib/rentalPricing';

const SHIPPING_CHARGE = 59; // Shipping charge for doorstep delivery

const CheckoutPage = () => {
  const { user, token } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [resume] = useState(() => (
    location.state?.cartItems?.length ? null : loadCheckoutResume()
  ));
  const checkoutState = location.state?.cartItems?.length ? location.state : (resume || {});
  const cartItems = checkoutState.cartItems || [];
  const weeklyTotalAmount = checkoutState.weeklyTotalAmount ?? checkoutState.totalAmount ?? 0;
  const passedStartDate = checkoutState.rentalStartDate;
  const passedEndDate = checkoutState.rentalEndDate;
  const passedDurationWeeks = checkoutState.rentalDurationWeeks;
  const passedRentalDays = checkoutState.rentalDays;
  const passedCouponCode = checkoutState.couponCode || '';
  const passedDiscount = checkoutState.discount || 0;
  const passedDeliveryMethod = checkoutState.deliveryMethod || 'pickup';

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token') || token}`,
  });
  
  const [deliveryAddress, setDeliveryAddress] = useState({
    house_flat_no: '',
    street: '',
    city: '',
    state: '',
    pincode: '',
    phone: '',
    gps_coordinates: null
  });
  
  const [rentalStartDate, setRentalStartDate] = useState('');
  const [rentalEndDate, setRentalEndDate] = useState('');
  const [rentalDuration, setRentalDuration] = useState(1); // weeks (API metadata)
  const [rentalDays, setRentalDays] = useState(passedRentalDays || 7);
  const [deliveryMethod, setDeliveryMethod] = useState(passedDeliveryMethod);
  const [couponCode, setCouponCode] = useState(passedCouponCode);
  const [discount, setDiscount] = useState(passedDiscount);
  const [appliedCoupon, setAppliedCoupon] = useState(passedCouponCode ? true : false);
  const [gstAmount, setGstAmount] = useState(0);
  const [platformFeeAmount, setPlatformFeeAmount] = useState(0);
  const [finalAmount, setFinalAmount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [detectingLocation, setDetectingLocation] = useState(false);
  const [currentOrderId, setCurrentOrderId] = useState(null);
  const [couponError, setCouponError] = useState('');
  const [otpDialogOpen, setOtpDialogOpen] = useState(false);
  const [otpValue, setOtpValue] = useState('');
  const [otpChannelHint, setOtpChannelHint] = useState('');
  const [otpChannel, setOtpChannel] = useState('email');
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpSending, setOtpSending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const idempotencyKeyRef = useRef(null);
  const placingOrderRef = useRef(false);
  const otpOrderIdRef = useRef(null);
  const otpPromiseRef = useRef(null);
  const [rentEligibility, setRentEligibility] = useState(null);
  const [showRentCriteria, setShowRentCriteria] = useState(false);

  const getShippingCharge = () => (deliveryMethod === 'delivery' ? SHIPPING_CHARGE : 0);

  useEffect(() => {
    const subtotal = Math.round((weeklyTotalAmount / 7) * rentalDays);
    const { platformFee, gst } = calculateRentalCharges(subtotal);
    const shipping = getShippingCharge();
    // Coupons only reduce the rental subtotal (same rule as the server)
    const cappedDiscount = Math.min(Math.max(0, Number(discount) || 0), subtotal);
    const total = subtotal + platformFee + gst + shipping - cappedDiscount;
    setPlatformFeeAmount(platformFee);
    setGstAmount(gst);
    setFinalAmount(Math.max(1, Math.round(total)));
  }, [rentalDays, weeklyTotalAmount, discount, deliveryMethod]);

  const getRentalSubtotal = () => Math.round((weeklyTotalAmount / 7) * rentalDays);

  const getItemLineTotal = (weeklyPrice, qty = 1) =>
    Math.round(((weeklyPrice || 0) / 7) * rentalDays * qty);

  const rememberCheckout = (eligibility) => {
    const shortfall = Math.max(
      0,
      Number(eligibility?.blockers?.[0]?.shortfall)
        || (Number(eligibility?.required_deposit || 0) - Number(eligibility?.deposit_balance || 0))
    );
    saveCheckoutResume({
      cartItems,
      weeklyTotalAmount,
      rentalStartDate: rentalStartDate || passedStartDate,
      rentalEndDate: rentalEndDate || passedEndDate,
      rentalDurationWeeks: rentalDuration || passedDurationWeeks,
      rentalDays,
      couponCode,
      discount,
      deliveryMethod,
      depositShortfall: shortfall,
    });
  };

  useEffect(() => {
    if (!cartItems || cartItems.length === 0) {
      toast.error('Your cart is empty');
      navigate('/cart');
      return;
    }
    
    if (user) {
      const addressParts = user.address ? user.address.split(', ') : ['', ''];
      setDeliveryAddress({
        house_flat_no: addressParts[0] || '',
        street: addressParts[1] || '',
        city: user.city || '',
        state: user.state || '',
        pincode: user.pincode || '',
        phone: user.phone || '',
        gps_coordinates: null
      });
    }
    
    if (passedStartDate && passedEndDate) {
      setRentalStartDate(passedStartDate);
      setRentalEndDate(passedEndDate);
    } else {
      const today = new Date();
      const startDate = new Date(today);
      startDate.setDate(startDate.getDate() + 1);
      const weeks = passedDurationWeeks || 1;
      const endDate = new Date(startDate);
      endDate.setDate(endDate.getDate() + weeks * 7);
      setRentalStartDate(startDate.toISOString().split('T')[0]);
      setRentalEndDate(endDate.toISOString().split('T')[0]);
    }
  }, [user, cartItems, navigate, passedStartDate, passedEndDate, passedDurationWeeks]);

  useEffect(() => {
    if (!user || !cartItems?.length) return;
    const tokenNow = localStorage.getItem('token') || token;
    checkRentEligibility(cartItems, tokenNow)
      .then((result) => {
        setRentEligibility(result);
        if (!result?.ok) {
          rememberCheckout(result);
          setShowRentCriteria(true);
        }
      })
      .catch(() => {});
  }, [user, token, cartItems]);

  useEffect(() => {
    if (rentalStartDate && rentalEndDate) {
      const start = new Date(rentalStartDate + 'T00:00:00');
      const end = new Date(rentalEndDate + 'T00:00:00');
      const diffDays = Math.round((end - start) / (1000 * 60 * 60 * 24));
      
      if (diffDays <= 0) return;

      // Validate max 360 days
      if (diffDays > 360) {
        toast.error('Rental period cannot exceed 360 days');
        const maxEnd = new Date(start);
        maxEnd.setDate(maxEnd.getDate() + 360);
        setRentalEndDate(maxEnd.toISOString().split('T')[0]);
        return;
      }
      
      const weeks = Math.max(1, Math.ceil(diffDays / 7));
      setRentalDuration(weeks);
      setRentalDays(diffDays);
    }
  }, [rentalStartDate, rentalEndDate]);

  const categoryDiscountSavings = cartItems.reduce((sum, item) => {
    if (item.discount_percentage > 0) {
      const qty = item.quantity || 1;
      return sum + Math.round((item.original_rental_price - item.discounted_rental_price) * qty);
    }
    return sum;
  }, 0);

  const detectGPSLocation = async () => {
    if (!navigator.geolocation) {
      toast.error('GPS is not supported by your browser');
      return;
    }
    
    setDetectingLocation(true);
    
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setDeliveryAddress(prev => ({
          ...prev,
          gps_coordinates: { lat: latitude, lng: longitude }
        }));
        toast.success('GPS location detected!');
        setDetectingLocation(false);
      },
      (error) => {
        toast.error('Failed to detect location. Please enter address manually.');
        setDetectingLocation(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleApplyCoupon = async () => {
    if (!couponCode) {
      setCouponError('Please enter a coupon code');
      return;
    }
    setCouponError('');
    try {
      const response = await axios.post(`${API}/validate-coupon`, {
        code: couponCode,
        total_amount: getRentalSubtotal()
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token') || token}` }
      });
      
      const subtotal = getRentalSubtotal();
      const raw = Number(response.data.discount_amount || response.data.discount || 0);
      setDiscount(Math.round(Math.min(Math.max(0, raw), subtotal)));
      setAppliedCoupon(true);
      setCouponError('');
      toast.success('Coupon applied successfully!');
    } catch (error) {
      const detail = error.response?.data?.detail;
      setCouponError(typeof detail === 'string' ? detail : 'Coupon Code not Valid');
    }
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const timer = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const resetPaymentUi = () => {
    setLoading(false);
    setPaymentProcessing(false);
    placingOrderRef.current = false;
    // Allow a clean retry with rebuilt totals after a failed payment attempt
    idempotencyKeyRef.current = null;
  };

  const sendPaymentOtp = async (orderId) => {
    setOtpSending(true);
    try {
      const response = await axios.post(
        `${API}/payments/otp/send`,
        { order_id: orderId },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token') || token}` } }
      );
      const channel = response.data.channel || 'email';
      const hint = response.data.email_hint || (
        channel === 'sms' || channel === 'whatsapp'
          ? (user?.phone || 'your phone')
          : (user?.email || 'your email')
      );
      setOtpChannelHint(hint);
      setOtpChannel(channel);
      setResendCooldown(response.data.resend_after_seconds || 60);
      const via = channel === 'whatsapp' ? 'WhatsApp' : channel === 'sms' ? 'phone (SMS)' : 'email';
      toast.success(`Verification code sent to your ${via}`);
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send verification code');
      return false;
    } finally {
      setOtpSending(false);
    }
  };

  const requestPaymentStepUp = (orderId) => {
    return new Promise((resolve, reject) => {
      otpOrderIdRef.current = orderId;
      otpPromiseRef.current = { resolve, reject };
      setOtpValue('');
      setOtpDialogOpen(true);
      sendPaymentOtp(orderId).then((ok) => {
        if (!ok) {
          setOtpDialogOpen(false);
          otpPromiseRef.current = null;
          reject(new Error('Failed to send verification code'));
        }
      });
    });
  };

  const handleOtpDialogClose = (open) => {
    if (open) {
      setOtpDialogOpen(true);
      return;
    }
    setOtpDialogOpen(false);
    if (otpPromiseRef.current) {
      otpPromiseRef.current.reject(new Error('Payment verification cancelled'));
      otpPromiseRef.current = null;
    }
  };

  const handleVerifyOtp = async () => {
    if (!otpOrderIdRef.current || otpValue.length !== 6) {
      toast.error('Enter the 6-digit verification code');
      return;
    }
    setOtpVerifying(true);
    try {
      const response = await axios.post(
        `${API}/payments/otp/verify`,
        { order_id: otpOrderIdRef.current, otp: otpValue },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token') || token}` } }
      );
      const stepUpToken = response.data.step_up_token;
      const pending = otpPromiseRef.current;
      otpPromiseRef.current = null;
      setOtpDialogOpen(false);
      pending?.resolve(stepUpToken);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid verification code');
    } finally {
      setOtpVerifying(false);
    }
  };

  const handleResendOtp = async () => {
    if (!otpOrderIdRef.current || resendCooldown > 0 || otpSending) return;
    setOtpValue('');
    await sendPaymentOtp(otpOrderIdRef.current);
  };

  const handlePlaceOrder = async () => {
    if (loading || paymentProcessing || placingOrderRef.current) return;
    placingOrderRef.current = true;
    
    if (deliveryMethod === 'delivery') {
      if (!deliveryAddress.house_flat_no || !deliveryAddress.street || !deliveryAddress.city || !deliveryAddress.pincode) {
        toast.error('Please fill in complete delivery address');
        placingOrderRef.current = false;
        return;
      }
      
      if (deliveryAddress.pincode.length !== 6) {
        toast.error('PIN code must be 6 digits');
        placingOrderRef.current = false;
        return;
      }
    }
    
    if (!rentalStartDate || !rentalEndDate) {
      toast.error('Please select rental start and end dates');
      placingOrderRef.current = false;
      return;
    }

    // MBR-122: block payment until deposit criteria are met
    try {
      const result = await checkRentEligibility(cartItems, localStorage.getItem('token') || token);
      setRentEligibility(result);
      if (!result?.ok) {
        rememberCheckout(result);
        setShowRentCriteria(true);
        placingOrderRef.current = false;
        return;
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not verify rent criteria');
      placingOrderRef.current = false;
      return;
    }
    
    setLoading(true);
    setPaymentProcessing(true);

    if (!idempotencyKeyRef.current) {
      idempotencyKeyRef.current = crypto.randomUUID();
    }
    
    try {
      const orderData = {
        items: cartItems.map(item => ({
          book_id: item.book_id,
          book_title: item.book.title,
          quantity: item.quantity,
          amount: getItemLineTotal(
            item.discounted_rental_price || item.book.rental_price_per_week,
            item.quantity
          )
        })),
        coupon_code: couponCode || null,
        delivery_address: deliveryAddress,
        delivery_method: deliveryMethod,
        rental_start_date: rentalStartDate,
        rental_end_date: rentalEndDate,
        rental_duration_weeks: rentalDuration,
        gst_amount: gstAmount,
        subtotal: getRentalSubtotal(),
        final_amount: finalAmount,
        idempotency_key: idempotencyKeyRef.current
      };
      
      // Step 1: Create order in database
      const orderResponse = await axios.post(`${API}/orders`, orderData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token') || token}` }
      });
      
      const order = orderResponse.data;
      setCurrentOrderId(order.id);

      // Step 1b: Payment step-up (email/SMS OTP) — required for all orders
      let stepUpToken = null;
      try {
        stepUpToken = await requestPaymentStepUp(order.id);
      } catch (otpError) {
        if (otpError?.message !== 'Payment verification cancelled') {
          toast.error(otpError.response?.data?.detail || otpError.message || 'Payment verification failed');
        } else {
          toast.info('Payment cancelled');
        }
        resetPaymentUi();
        return;
      }
      
      // Step 2: Initialize Razorpay payment
      let paymentInitResponse;
      try {
        paymentInitResponse = await axios.post(
          `${API}/payments/razorpay/init`,
          {
            order_id: order.id,
            ...(stepUpToken ? { step_up_token: stepUpToken } : {})
          },
          { headers: { Authorization: `Bearer ${localStorage.getItem('token') || token}` }}
        );
      } catch (initError) {
        const errorMsg = initError.response?.data?.detail || 'Payment gateway error. Please try again later.';
        toast.error(errorMsg);
        resetPaymentUi();
        return;
      }
      
      const { razorpay_order_id, amount, currency, key_id } = paymentInitResponse.data;
      
      // Step 3: Load Razorpay script
      const scriptLoaded = await loadRazorpayScript();
      
      if (!scriptLoaded) {
        toast.error('Failed to load payment gateway. Please try again.');
        resetPaymentUi();
        return;
      }
      
      // Step 4: Open Razorpay checkout modal
      let paymentCompleted = false;

      const options = {
        key: key_id,
        amount: amount,
        currency: currency,
        name: 'MyBook.Rent',
        description: `Rental Order - ${cartItems.length} book(s) for ${rentalDays} day(s)`,
        order_id: razorpay_order_id,
        handler: async function (response) {
          paymentCompleted = true;
          // Step 5: Verify payment on backend
          try {
            const verifyRes = await axios.post(
              `${API}/payments/razorpay/verify`,
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                order_id: order.id
              },
              { headers: { Authorization: `Bearer ${localStorage.getItem('token') || token}` }}
            );
            
            await axios.delete(`${API}/cart/clear`, {
              headers: { Authorization: `Bearer ${localStorage.getItem('token') || token}` }
            }).catch(() => {});
            clearCheckoutResume();
            
            if (verifyRes.data?.lender_approval_pending) {
              toast.success('Payment received! Waiting for lender approval.');
            } else {
              toast.success('Payment successful! Order confirmed.');
            }
            navigate(`/order-success/${order.id}`);
          } catch (error) {
            toast.error('Payment verification failed. Please contact support.');
            navigate('/');
          } finally {
            setPaymentProcessing(false);
            setLoading(false);
          }
        },
        prefill: {
          name: user?.name || '',
          email: user?.email || '',
          contact: deliveryAddress.phone || user?.phone || ''
        },
        theme: {
          color: '#A89968'
        },
        modal: {
          ondismiss: async function() {
            if (paymentCompleted) return;
            try {
              await axios.post(
                `${API}/payments/razorpay/fail`,
                { order_id: order.id },
                { headers: { Authorization: `Bearer ${localStorage.getItem('token') || token}` }}
              );
            } catch (e) { /* ignore */ }
            toast.info('Payment cancelled');
            resetPaymentUi();
          }
        }
      };
      
      const razorpay = new window.Razorpay(options);
      
      // Razorpay fires this on each failed attempt within the modal; user may retry.
      // Only handle terminal failure when the modal is dismissed (ondismiss above).
      razorpay.on('payment.failed', function () {
        // Intentionally no toast or state reset — Razorpay shows its own retry UI.
      });
      
      razorpay.open();
      
    } catch (error) {
      console.error('Order error:', error);
      toast.error(error.response?.data?.detail || 'Failed to place order');
      resetPaymentUi();
    }
  };

  return (
    <div className="min-h-screen" style={{background: 'var(--background)'}}>
      <Navbar />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-4xl font-bold mb-8" style={{color: 'var(--primary)'}}>Checkout</h1>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            {/* Delivery Method */}
            <div className="bg-white rounded-2xl shadow-md p-6">
              <h2 className="text-2xl font-bold mb-4 flex items-center">
                <Truck className="h-6 w-6 mr-2" style={{color: 'var(--primary)'}} />
                Delivery Method
              </h2>
              
              <RadioGroup value={deliveryMethod} onValueChange={setDeliveryMethod}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div 
                    className={`flex items-center space-x-2 p-4 border-2 rounded-xl cursor-pointer transition-all ${
                      deliveryMethod === 'pickup' ? 'border-amber-600 bg-amber-50' : 'border-gray-200'
                    }`}
                    onClick={() => setDeliveryMethod('pickup')}
                    data-testid="checkout-pickup-option"
                  >
                    <RadioGroupItem value="pickup" id="pickup" />
                    <Label htmlFor="pickup" className="cursor-pointer flex-1">
                      <div className="font-semibold flex items-center">
                        <User className="h-4 w-4 mr-2" />
                        Self Pickup
                      </div>
                      <div className="text-sm text-gray-500">Collect from book owner · Free</div>
                    </Label>
                  </div>

                  <div 
                    className={`flex items-center space-x-2 p-4 border-2 rounded-xl cursor-pointer transition-all ${
                      deliveryMethod === 'delivery' ? 'border-amber-600 bg-amber-50' : 'border-gray-200'
                    }`}
                    onClick={() => setDeliveryMethod('delivery')}
                    data-testid="checkout-delivery-option"
                  >
                    <RadioGroupItem value="delivery" id="delivery" />
                    <Label htmlFor="delivery" className="cursor-pointer flex-1">
                      <div className="font-semibold flex items-center">
                        <Truck className="h-4 w-4 mr-2" />
                        Home Delivery
                      </div>
                      <div className="text-sm text-gray-500">Shiprocket · +₹{SHIPPING_CHARGE}</div>
                    </Label>
                  </div>
                </div>
              </RadioGroup>
              
              {deliveryMethod === 'pickup' && (
                <div className="mt-4 p-4 bg-amber-50 rounded-lg">
                  <p className="text-sm text-amber-800">
                    <strong>Note:</strong> A unique pickup code will be generated after payment. 
                    Share this code with the book owner to collect your book securely. No courier is booked.
                  </p>
                </div>
              )}
              {deliveryMethod === 'delivery' && (
                <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Note:</strong> The book is picked up from the owner&apos;s profile address and delivered to yours via Shiprocket.
                  </p>
                </div>
              )}
            </div>

            {/* Delivery Address - Only show for delivery method */}
            {deliveryMethod === 'delivery' && (
              <div className="bg-white rounded-2xl shadow-md p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-bold flex items-center">
                    <MapPin className="h-6 w-6 mr-2" style={{color: 'var(--primary)'}} />
                    Delivery Address
                  </h2>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={detectGPSLocation}
                    disabled={detectingLocation}
                  >
                    <Navigation className="h-4 w-4 mr-2" />
                    {detectingLocation ? 'Detecting...' : 'Use GPS'}
                  </Button>
                </div>
                
                {deliveryAddress.gps_coordinates && (
                  <div className="mb-4 p-2 bg-green-50 rounded-lg text-sm text-green-700">
                    📍 GPS Location: {deliveryAddress.gps_coordinates.lat.toFixed(6)}, {deliveryAddress.gps_coordinates.lng.toFixed(6)}
                  </div>
                )}
                
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>House/Flat No. *</Label>
                      <Input
                        value={deliveryAddress.house_flat_no}
                        onChange={(e) => setDeliveryAddress({...deliveryAddress, house_flat_no: e.target.value})}
                        placeholder="House/Flat No."
                      />
                    </div>
                    <div>
                      <Label>Phone Number *</Label>
                      <Input
                        value={deliveryAddress.phone}
                        onChange={(e) => setDeliveryAddress({...deliveryAddress, phone: e.target.value.replace(/\D/g, '')})}
                        placeholder="10-digit phone"
                        maxLength={10}
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label>Street Address *</Label>
                    <Input
                      value={deliveryAddress.street}
                      onChange={(e) => setDeliveryAddress({...deliveryAddress, street: e.target.value})}
                      placeholder="Street Address"
                    />
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>City *</Label>
                      <Input
                        value={deliveryAddress.city}
                        onChange={(e) => setDeliveryAddress({...deliveryAddress, city: e.target.value})}
                        placeholder="City"
                      />
                    </div>
                    <div>
                      <Label>State</Label>
                      <Input
                        value={deliveryAddress.state}
                        onChange={(e) => setDeliveryAddress({...deliveryAddress, state: e.target.value})}
                        placeholder="State"
                      />
                    </div>
                    <div>
                      <Label>PIN Code *</Label>
                      <Input
                        value={deliveryAddress.pincode}
                        onChange={(e) => setDeliveryAddress({...deliveryAddress, pincode: e.target.value.replace(/\D/g, '')})}
                        placeholder="6 digits"
                        maxLength={6}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Rental Duration with Date Picker */}
            <div className="bg-white rounded-2xl shadow-md p-6">
              <h2 className="text-2xl font-bold mb-4 flex items-center">
                <Calendar className="h-6 w-6 mr-2" style={{color: 'var(--primary)'}} />
                Rental Period
              </h2>
              
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <Label>Start Date *</Label>
                  <Input
                    type="date"
                    value={rentalStartDate}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (!val) return;
                      setRentalStartDate(val);
                    }}
                    min={(() => {
                      const d = new Date();
                      d.setDate(d.getDate() + 1);
                      return d.toISOString().split('T')[0];
                    })()}
                  />
                </div>
                <div>
                  <Label>End Date *</Label>
                  <Input
                    type="date"
                    value={rentalEndDate}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (!val) return;
                      setRentalEndDate(val);
                    }}
                    min={rentalStartDate}
                    max={rentalStartDate ? (() => {
                      const d = new Date(rentalStartDate + 'T00:00:00');
                      d.setDate(d.getDate() + 360);
                      return d.toISOString().split('T')[0];
                    })() : undefined}
                  />
                </div>
              </div>
              
              <div className="p-4 bg-gray-50 rounded-lg space-y-3">
                <div className="flex justify-between items-center">
                  <span className="font-medium">Rental Duration:</span>
                  <span className="text-lg font-bold" style={{color: 'var(--primary)'}} data-testid="rental-days">
                    {rentalDays} day{rentalDays !== 1 ? 's' : ''}
                  </span>
                </div>
                <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 flex items-start space-x-2">
                  <span className="text-amber-600 font-bold">!</span>
                  <div>
                    <p className="text-sm text-amber-800 font-medium">Important Note</p>
                    <p className="text-xs text-amber-700">
                      Extra days beyond the end date ({rentalEndDate || 'selected end date'}) will be charged at
                      ₹{Math.round(weeklyTotalAmount / 7)}/day as late fee.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Coupon Code */}
            <div className="bg-white rounded-2xl shadow-md p-6">
              <h2 className="text-2xl font-bold mb-4">Apply Coupon</h2>
              {appliedCoupon ? (
                <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <span className="text-green-600 font-semibold">✓ Coupon Applied:</span>
                    <span className="font-mono font-bold">{couponCode}</span>
                    <span className="text-green-600">(-₹{discount})</span>
                  </div>
                  <button 
                    onClick={() => { setAppliedCoupon(false); setDiscount(0); setCouponCode(''); }}
                    className="text-red-600 text-sm hover:underline"
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <div>
                  <div className="flex space-x-2">
                    <Input
                      value={couponCode}
                      onChange={(e) => { setCouponCode(e.target.value.toUpperCase()); setCouponError(''); }}
                      placeholder="Enter coupon code"
                      data-testid="coupon-input"
                    />
                    <Button onClick={handleApplyCoupon} style={{backgroundColor: 'var(--accent)', color: 'white'}} data-testid="apply-coupon-button">
                      Apply
                    </Button>
                  </div>
                  {couponError && (
                    <p className="text-red-600 text-sm font-medium mt-2" data-testid="coupon-error">{couponError}</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-md p-6 sticky top-24">
              <h2 className="text-2xl font-bold mb-4">Order Summary</h2>
              
              <div className="space-y-3">
                {cartItems.map((item, idx) => (
                  <div key={idx} className="text-sm font-medium text-gray-900">
                    {item.book.title}
                    {(item.quantity || 1) > 1 && <span className="text-gray-500"> x{item.quantity}</span>}
                  </div>
                ))}
              </div>

              <div className="border-t mt-4 pt-4 space-y-3">
                <div className="flex justify-between items-start">
                  <div>
                    <span className="font-medium text-gray-900">Rental amount</span>
                    <span className="text-gray-500 text-sm block">
                      ₹{Math.round(weeklyTotalAmount / 7)}/day × {rentalDays} day{rentalDays !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <span className="font-medium">₹{getRentalSubtotal()}</span>
                </div>

                {categoryDiscountSavings > 0 && (
                  <div className="flex justify-between text-sm text-green-600" data-testid="category-discount-line">
                    <span>Category Discount</span>
                    <span>-₹{Math.round((categoryDiscountSavings / 7) * rentalDays)} saved</span>
                  </div>
                )}

                <div className="flex justify-between text-gray-700" data-testid="platform-fee-line">
                  <span>Platform fee</span>
                  <span>₹{platformFeeAmount}</span>
                </div>

                <div className="flex justify-between text-gray-700" data-testid="gst-line">
                  <span>GST</span>
                  <span>₹{gstAmount}</span>
                </div>
                
                {deliveryMethod === 'delivery' && (
                  <div className="flex justify-between text-sm text-gray-600">
                    <span className="flex items-center">
                      <Truck className="h-4 w-4 mr-1" />
                      Shipping Charges
                    </span>
                    <span>₹{SHIPPING_CHARGE}</span>
                  </div>
                )}
                
                {discount > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>Discount</span>
                    <span>-₹{Math.min(discount, getRentalSubtotal())}</span>
                  </div>
                )}
                
                <div className="border-t pt-2 flex justify-between font-bold text-lg">
                  <span>Total</span>
                  <span style={{color: 'var(--accent)'}}>₹{finalAmount}</span>
                </div>
              </div>
              
              <Button
                onClick={handlePlaceOrder}
                disabled={loading || paymentProcessing}
                className="w-full mt-6 h-14 text-lg hover:opacity-90"
                style={{backgroundColor: 'var(--primary)', color: 'white'}}
                data-testid="proceed-payment-button"
              >
                <CreditCard className="h-5 w-5 mr-2" />
                {paymentProcessing ? 'Payment in Progress...' : loading ? 'Processing...' : 'Proceed to Payment'}
              </Button>
              
              <p className="text-xs text-gray-500 text-center mt-4">
                By proceeding, you agree to our Terms of Service and Privacy Policy. 
                All amounts include applicable GST.
              </p>
            </div>
          </div>
        </div>
      </div>
      <RentCriteriaDialog
        open={showRentCriteria}
        onOpenChange={setShowRentCriteria}
        eligibility={rentEligibility}
      />
      <Dialog open={otpDialogOpen} onOpenChange={handleOtpDialogClose}>
        <DialogContent className="sm:max-w-md bg-white border border-gray-200 shadow-2xl rounded-2xl p-6" style={{ backgroundColor: '#ffffff', color: '#111827' }} onPointerDownOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-gray-900">Verify your payment</DialogTitle>
            <DialogDescription className="text-sm text-gray-600 mt-1">
              For your security, enter the 6-digit payment verification code
              sent to{' '}
              <span className="font-semibold text-gray-800">
                {otpChannel === 'whatsapp'
                  ? `WhatsApp ${otpChannelHint || ''}`.trim()
                  : otpChannel === 'sms'
                    ? `SMS ${otpChannelHint || ''}`.trim()
                    : (otpChannelHint || 'your account email')}
              </span>
              . This is required before Razorpay opens.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center gap-4 py-3">
            <InputOTP
              maxLength={6}
              value={otpValue}
              onChange={setOtpValue}
              disabled={otpVerifying || otpSending}
            >
              <InputOTPGroup className="gap-1.5">
                <InputOTPSlot index={0} className="w-11 h-12 text-lg font-bold border rounded-lg border-gray-300 bg-gray-50 focus:bg-white text-gray-900" />
                <InputOTPSlot index={1} className="w-11 h-12 text-lg font-bold border rounded-lg border-gray-300 bg-gray-50 focus:bg-white text-gray-900" />
                <InputOTPSlot index={2} className="w-11 h-12 text-lg font-bold border rounded-lg border-gray-300 bg-gray-50 focus:bg-white text-gray-900" />
                <InputOTPSlot index={3} className="w-11 h-12 text-lg font-bold border rounded-lg border-gray-300 bg-gray-50 focus:bg-white text-gray-900" />
                <InputOTPSlot index={4} className="w-11 h-12 text-lg font-bold border rounded-lg border-gray-300 bg-gray-50 focus:bg-white text-gray-900" />
                <InputOTPSlot index={5} className="w-11 h-12 text-lg font-bold border rounded-lg border-gray-300 bg-gray-50 focus:bg-white text-gray-900" />
              </InputOTPGroup>
            </InputOTP>
            <button
              type="button"
              className="text-sm font-medium underline disabled:no-underline disabled:opacity-50 transition-colors"
              style={{ color: 'var(--primary)' }}
              onClick={handleResendOtp}
              disabled={resendCooldown > 0 || otpSending || otpVerifying}
            >
              {otpSending
                ? 'Sending…'
                : resendCooldown > 0
                  ? `Resend code in ${resendCooldown}s`
                  : 'Resend code'}
            </button>
          </div>
          <DialogFooter className="gap-2 sm:gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOtpDialogClose(false)}
              disabled={otpVerifying}
              className="border-gray-300 text-gray-700 hover:bg-gray-100"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleVerifyOtp}
              disabled={otpValue.length !== 6 || otpVerifying || otpSending}
              style={{ backgroundColor: 'var(--primary)', color: 'white' }}
              className="px-6 font-semibold shadow-md"
            >
              {otpVerifying ? 'Verifying…' : 'Verify & continue'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Footer />
    </div>
  );
};

export default CheckoutPage;
